import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type PlatformPackage = {
  id: string;
  name: string;
  description: string | null;
  price_try_kurus: number | null;
  price_usd_cents: number | null;
  credits: number;
  duration_days: number;
  features_json: string | null;
  stripe_price_id: string | null;
  iyzico_product_id: string | null;
  sort_order: number;
  enabled: number | boolean;
  created_at: number;
};

export type PackageInput = {
  id: string;
  name: string;
  description?: string;
  price_try_kurus?: number | null;
  price_usd_cents?: number | null;
  credits: number;
  duration_days?: number;
  features_json?: string;
  stripe_price_id?: string;
  iyzico_product_id?: string;
  sort_order?: number;
  enabled?: boolean;
};

export function usePlatformPackages() {
  return useQuery({
    queryKey: ['platform', 'packages'],
    queryFn: () => api.get<{ packages: PlatformPackage[] }>('/api/platform/packages'),
  });
}

export function useCreatePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PackageInput) => api.post<{ id: string }>('/api/platform/packages', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'packages'] }),
  });
}

export function useUpdatePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Partial<Omit<PackageInput, 'id'>>) =>
      api.put<{ updated: number }>(`/api/platform/packages/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'packages'] }),
  });
}

export function useDeletePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ disabled: boolean }>(`/api/platform/packages/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'packages'] }),
  });
}
